import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Box,
  Typography,
  Alert,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  IconButton,
  InputAdornment,
  Switch,
} from "@mui/material";
import { Delete, Edit, Save, Close } from "@mui/icons-material";
import {
  Services,
  Produtos,
  IPetPopulated,
  IUser,
  IPedido,
  IItem,
} from "../types";
import {
  createAttachment,
  getPets,
  searchExternalServices,
  searchExternalProducts,
  createSchedule,
} from "../services";

interface ModalNewAtendimentoProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  tecnico: IUser | null;
  initialPet?: IPetPopulated | null;
}

const ModalNewAtendimento: React.FC<ModalNewAtendimentoProps> = ({
  open,
  onClose,
  onSave,
  tecnico,
  initialPet,
}) => {
  const [pets, setPets] = useState<IPetPopulated[]>([]);
  const [selectedService, setSelectedService] = useState<Services | null>(null);
  const [selectedProdutos, setSelectedProdutos] = useState<Produtos[]>([]);
  const [selectedPet, setSelectedPet] = useState<IPetPopulated | null>(null);
  const [historyNotes, setHistoryNotes] = useState("");
  const [selectData, setSelectData] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estados para edição de preços
  const [editingServicePrice, setEditingServicePrice] = useState(false);
  const [tempServicePrice, setTempServicePrice] = useState<string>("");
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [tempProductPrice, setTempProductPrice] = useState<string>("");

  // Estados para pagamento
  const [pagamentoQuitado, setPagamentoQuitado] = useState(false);
  const [valorPago, setValorPago] = useState<number>(0);
  const [formaPagamento, setFormaPagamento] = useState<string>("Dinheiro");
  const [contaBancaria, setContaBancaria] = useState<string>("Caixa Principal");
  const [dataVencimento, setDataVencimento] = useState<string>("");

  // Estados para pesquisa externa de serviços
  const [externalServices, setExternalServices] = useState<any[]>([]);
  const [isSearchingServices, setIsSearchingServices] = useState(false);
  const [serviceSearchTerm, setServiceSearchTerm] = useState("");

  // Estados para pesquisa externa de produtos
  const [externalProducts, setExternalProducts] = useState<any[]>([]);
  const [isSearchingProducts, setIsSearchingProducts] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState("");

  // Estados para criar agendamento junto
  const [createAgendamento, setCreateAgendamento] = useState(false);
  const [agendamentoData, setAgendamentoData] = useState<string>("");
  const [agendamentoHora, setAgendamentoHora] = useState<string>("");
  const [agendamentoObservacoes, setAgendamentoObservacoes] = useState("");
  const [requiresPickup, setRequiresPickup] = useState(false);

  useEffect(() => {
    if (open) {
      // Reset states when modal opens
      setSelectedService(null);
      setSelectedProdutos([]);
      setSelectedPet(initialPet || null); // Define o pet inicial se fornecido
      setHistoryNotes("");
      setSelectData("");
      setError(null);
      setPagamentoQuitado(false);
      setValorPago(0);
      setFormaPagamento("Dinheiro");
      setContaBancaria("Caixa Principal");
      setDataVencimento("");
      // Reset estados de pesquisa externa
      setExternalServices([]);
      setServiceSearchTerm("");
      setIsSearchingServices(false);
      setExternalProducts([]);
      setProductSearchTerm("");
      setIsSearchingProducts(false);
      // Reset estados de agendamento
      setCreateAgendamento(false);
      setAgendamentoData("");
      setAgendamentoHora("");
      setAgendamentoObservacoes("");
      setRequiresPickup(false);
      loadData();
    }
  }, [open, initialPet]);

  // Funções de formatação de moeda
  const formatarMoeda = (valor: number): string => {
    return (valor / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleValorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorDigitado = e.target.value.replace(/[^\d]/g, "");
    const valorNumerico = parseInt(valorDigitado || "0", 10);
    setValorPago(valorNumerico);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Permitir: backspace, delete, tab, escape, enter
    if (
      [8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
      // Permitir: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)
    ) {
      return;
    }
    // Permitir apenas números
    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  // Função para pesquisar serviços externos
  const searchServices = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setExternalServices([]);
      return;
    }

    setIsSearchingServices(true);
    try {
      const results = await searchExternalServices(searchTerm);
      setExternalServices(results);
    } catch (error) {
      setExternalServices([]);
    } finally {
      setIsSearchingServices(false);
    }
  };

  // Função para pesquisar produtos externos
  const searchProducts = async (searchTerm: string) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      setExternalProducts([]);
      return;
    }

    setIsSearchingProducts(true);
    try {
      const results = await searchExternalProducts(searchTerm);
      setExternalProducts(results);
    } catch (error) {
      setExternalProducts([]);
    } finally {
      setIsSearchingProducts(false);
    }
  };

  // Debounce para pesquisa de serviços
  useEffect(() => {
    const timer = setTimeout(() => {
      if (serviceSearchTerm) {
        searchServices(serviceSearchTerm);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [serviceSearchTerm]);

  // Debounce para pesquisa de produtos
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearchTerm) {
        searchProducts(productSearchTerm);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  // Atualizar valor pago automaticamente quando quitado
  useEffect(() => {
    if (pagamentoQuitado) {
      const valorServico = selectedService
        ? getEffectivePrice(selectedService)
        : 0;
      const valorProdutos = selectedProdutos.reduce(
        (sum, produto) => sum + getEffectivePrice(produto),
        0
      );
      const valorTotal = valorServico + valorProdutos;
      // Converter para centavos para compatibilidade com o sistema de moeda inteligente
      setValorPago(Math.round(valorTotal * 100));
    }
  }, [pagamentoQuitado, selectedService, selectedProdutos]);

  const loadData = async () => {
    try {
      const [petsData] = await Promise.allSettled([getPets()]);

      if (petsData.status === "fulfilled" && petsData.value) {
        setPets(petsData.value as IPetPopulated[]);
      }
    } catch (error) {
      setError("Erro ao carregar dados necessários");
    }
  };

  // Função para obter o preço efetivo (editado ou original)
  const getEffectivePrice = (item: Services | Produtos): number => {
    return item.editedPrice !== undefined ? item.editedPrice : item.PrecoVenda;
  };

  // Funções para edição de preço do serviço
  const handleEditServicePrice = () => {
    if (selectedService) {
      setTempServicePrice(
        getEffectivePrice(selectedService).toFixed(2).replace(".", ",")
      );
      setEditingServicePrice(true);
    }
  };

  const handleSaveServicePrice = () => {
    if (selectedService && tempServicePrice) {
      const newPrice = parseFloat(tempServicePrice.replace(",", "."));
      if (!isNaN(newPrice) && newPrice > 0) {
        setSelectedService({
          ...selectedService,
          editedPrice: newPrice,
          originalPrice:
            selectedService.originalPrice || selectedService.PrecoVenda,
        });
      }
      setEditingServicePrice(false);
      setTempServicePrice("");
    }
  };

  const handleCancelServicePriceEdit = () => {
    setEditingServicePrice(false);
    setTempServicePrice("");
  };

  // Funções para edição de preço de produtos
  const handleEditProductPrice = (produto: Produtos) => {
    setEditingProductId(produto.ID);
    setTempProductPrice(
      getEffectivePrice(produto).toFixed(2).replace(".", ",")
    );
  };

  const handleSaveProductPrice = (produtoId: string) => {
    if (tempProductPrice) {
      const newPrice = parseFloat(tempProductPrice.replace(",", "."));
      if (!isNaN(newPrice) && newPrice > 0) {
        setSelectedProdutos((prev) =>
          prev.map((p) =>
            p.ID === produtoId
              ? {
                  ...p,
                  editedPrice: newPrice,
                  originalPrice: p.originalPrice || p.PrecoVenda,
                }
              : p
          )
        );
      }
    }
    setEditingProductId(null);
    setTempProductPrice("");
  };

  const handleCancelProductPriceEdit = () => {
    setEditingProductId(null);
    setTempProductPrice("");
  };

  const removeProduto = (produtoId: string) => {
    setSelectedProdutos((prev) => prev.filter((p) => p.ID !== produtoId));
  };

  const handleSubmit = async () => {
    if (!selectedService || !selectedPet) {
      setError("Serviço e Pet são obrigatórios");
      return;
    }

    // Validar campos de agendamento se a opção estiver marcada
    if (createAgendamento) {
      if (!agendamentoData || !agendamentoHora) {
        setError("Por favor, preencha a data e hora do agendamento");
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const items: IItem[] = [];

      // Adiciona o serviço com preço editado (se aplicável)
      const servicePrice = getEffectivePrice(selectedService);
      items.push({
        Codigo: selectedService.Codigo,
        Unidade: selectedService.UnidadeComercial,
        Descricao: selectedService.Nome,
        Quantidade: 1,
        ValorUnitario: servicePrice,
        ValorFrete: 0,
        DescontoUnitario: 0,
        ValorTotal: servicePrice,
        PesoKG: selectedService.PesoKG,
        Comprimento: selectedService.Comprimento,
        Altura: selectedService.Altura,
        Largura: selectedService.Largura,
        FreteGratis: false,
        ValorUnitarioFrete: 0,
        PrazoEntregaFrete: 0,
        ComissaoVendedor: 0,
        Seguro: 0,
        AgrupamentoItensVenda: "Grupo de Produtos Padrão - 01",
      });

      // Adiciona produtos selecionados com preços editados (se aplicável)
      selectedProdutos.forEach((produto) => {
        const productPrice = getEffectivePrice(produto);
        items.push({
          Codigo: produto.Codigo,
          Unidade: produto.UnidadeComercial,
          Descricao: produto.Nome,
          Quantidade: 1,
          ValorUnitario: productPrice,
          ValorFrete: 0,
          DescontoUnitario: 0,
          ValorTotal: productPrice,
          PesoKG: produto.PesoKG,
          Comprimento: produto.Comprimento,
          Altura: produto.Altura,
          Largura: produto.Largura,
          FreteGratis: false,
          ValorUnitarioFrete: 0,
          PrazoEntregaFrete: 0,
          ComissaoVendedor: 0,
          Seguro: 0,
          AgrupamentoItensVenda: "Grupo de Produtos Padrão - 01",
        });
      });

      const dataToSend: IPedido = {
        OrigemVenda: "Venda Direta",
        DepositoID: "683df6ce93591480c838eb1b",
        Deposito: "PADRÃO",
        StatusSistema: "Pedido",
        Categoria: "Venda de Serviços",
        Descricao: selectedPet.name,
        Validade: new Date().toISOString(),
        Empresa: "PETSHOP DA MADRE",
        EmpresaID: "683df6ce93591480c838eb1a",
        ClienteID: selectedPet.tutor.idERP || selectedPet.tutor.erpId || "",
        Cliente: selectedPet.tutor.name,
        PessoaID: selectedPet.tutor.idERP || selectedPet.tutor.erpId || "",
        ClienteCNPJ: selectedPet.tutor.cpf,
        ClienteEmail: selectedPet.tutor.email ?? "",
        Vendedor: tecnico?.NomeFantasia ?? "",
        DataEnvio: new Date().toISOString(),
        Municipio: "Londrina",
        CodigoMunicipio: "4113700",
        Pais: "Brasil",
        Cep: selectedPet.tutor.cep || selectedPet.tutor.addressZip || "",
        Uf: "PR",
        UfCodigo: "41",
        Bairro:
          selectedPet.tutor.bairro || selectedPet.tutor.addressDistrict || "",
        Logradouro:
          selectedPet.tutor.logradouro || selectedPet.tutor.addressStreet || "",
        Items: items,
        Data: selectData || new Date().toISOString(),
        LancarComissaoVendedor: false,
        OrdemServico: {
          OrdemServicoConcluida: false,
          OrdemServicoCancelada: false,
          Problema: historyNotes,
          Laudo: historyNotes,
          DataAbertura: new Date().toISOString(),
          Tecnico: tecnico?.NomeFantasia ?? "",
          DataVisita: selectData || new Date().toISOString(),
          DataInicioIntervencaoTecnica: selectData || new Date().toISOString(),
          Laudos: [
            {
              Tecnico: tecnico?.NomeFantasia ?? "",
              DataRegistro: new Date().toISOString(),
              LaudoTecnicoGeral: historyNotes,
            },
          ],
        },
        Atributos: [
          {
            Nome: "PET",
            Model: selectedPet.name,
          },
        ],
        // Campos para lançamento financeiro
        pagamentoQuitado,
        valorPago: valorPago / 100, // Converter de centavos para reais
        formaPagamento,
        contaBancaria,
        dataVencimento,
      };

      await createAttachment(dataToSend);

      // Obter o ID do pet (pode ser _id ou id)
      const petId = selectedPet._id || selectedPet.id;

      // Debug: verificar valores antes do if

      // Se a opção de criar agendamento estiver marcada, criar o agendamento
      if (createAgendamento && agendamentoData && agendamentoHora && petId) {
        try {
          // Combinar data e hora para criar o timestamp
          const scheduledDateTime = new Date(
            `${agendamentoData}T${agendamentoHora}:00`
          );

          const scheduleData = {
            petId,
            serviceName: selectedService.Nome,
            serviceExternalId: selectedService.ID,
            scheduledAt: scheduledDateTime.toISOString(),
            status: "PENDENTE",
            requiresPickup,
            isRecurring: false,
            notes: agendamentoObservacoes,
            createNotification: true, // Criar notificação automática
          };

          const agendamentoResult = await createSchedule(scheduleData);
        } catch (scheduleError: any) {
          // Mostrar erro ao usuário e não fechar o modal
          setError(
            `Atendimento criado, mas houve um erro ao criar o agendamento: ${
              scheduleError.response?.data?.message ||
              scheduleError.message ||
              "Erro desconhecido"
            }`
          );
          setIsSubmitting(false);
          return; // Não fechar o modal se houver erro no agendamento
        }
      }

      onSave();
      handleClose();
    } catch (error) {
      setError("Erro ao criar atendimento. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedService(null);
    setSelectedProdutos([]);
    setSelectedPet(null);
    setHistoryNotes("");
    setSelectData("");
    setError(null);
    // Reset estados de agendamento
    setCreateAgendamento(false);
    setAgendamentoData("");
    setAgendamentoHora("");
    setAgendamentoObservacoes("");
    setRequiresPickup(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Novo Atendimento</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={12}>
            <Autocomplete
              options={pets}
              getOptionLabel={(option) =>
                `${option.name} - ${option.tutor.name}`
              }
              value={selectedPet}
              onChange={(_, newValue) => {
                setSelectedPet(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Pet *"
                  placeholder="Pesquise pelo nome do pet ou tutor..."
                  required
                />
              )}
              filterOptions={(options, { inputValue }) => {
                const lowerInput = inputValue.toLowerCase();
                return options.filter(
                  (pet) =>
                    pet.name.toLowerCase().includes(lowerInput) ||
                    pet.tutor.name.toLowerCase().includes(lowerInput)
                );
              }}
              noOptionsText="Nenhum pet encontrado"
            />
          </Grid>

          <Grid size={12}>
            <Autocomplete
              options={serviceSearchTerm.length >= 2 ? externalServices : []}
              getOptionLabel={(option) => {
                return `${option.Nome} - R$ ${option.PrecoVenda.toFixed(2)}`;
              }}
              value={selectedService}
              onChange={(_, newValue) => {
                setSelectedService(newValue);
              }}
              onInputChange={(_, newInputValue) => {
                setServiceSearchTerm(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Serviço *"
                  placeholder="Digite para pesquisar serviços..."
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearchingServices && (
                          <Typography variant="caption" sx={{ mr: 1 }}>
                            Pesquisando...
                          </Typography>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              filterOptions={(options) => options} // Não filtrar aqui, já filtramos na API
              noOptionsText={
                isSearchingServices
                  ? "Pesquisando..."
                  : serviceSearchTerm.length < 2
                    ? "Digite pelo menos 2 caracteres para pesquisar"
                    : "Nenhum serviço encontrado"
              }
              loading={isSearchingServices}
            />
            {/* Display selected service with price edit option */}
            {selectedService && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Serviço Selecionado:
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    p: 1,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ flex: 1 }}>
                    <strong>{selectedService.Nome}</strong>
                  </Typography>
                  {!editingServicePrice ? (
                    <>
                      <Typography variant="body2" color="primary.main">
                        R$ {getEffectivePrice(selectedService).toFixed(2)}
                      </Typography>
                      {selectedService.editedPrice !== undefined && (
                        <Typography
                          variant="caption"
                          sx={{
                            textDecoration: "line-through",
                            color: "text.secondary",
                          }}
                        >
                          R$ {selectedService.PrecoVenda.toFixed(2)}
                        </Typography>
                      )}
                      <IconButton
                        size="small"
                        onClick={handleEditServicePrice}
                        color="primary"
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <TextField
                        size="small"
                        value={tempServicePrice}
                        onChange={(e) => setTempServicePrice(e.target.value)}
                        placeholder="0,00"
                        sx={{ width: 120 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">R$</InputAdornment>
                          ),
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={handleSaveServicePrice}
                        color="success"
                      >
                        <Save fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={handleCancelServicePriceEdit}
                        color="error"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Grid>

          <Grid size={12}>
            <Autocomplete
              options={productSearchTerm.length >= 2 ? externalProducts : []}
              getOptionLabel={(option) =>
                `${option.Nome} - R$ ${option.PrecoVenda.toFixed(2)}`
              }
              value={null}
              onChange={(_, newValue) => {
                if (newValue) {
                  setSelectedProdutos((prev) => [...prev, newValue]);
                }
              }}
              onInputChange={(_, newInputValue) => {
                setProductSearchTerm(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Adicionar Produto"
                  placeholder="Digite para pesquisar produtos..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isSearchingProducts && (
                          <Typography variant="caption" sx={{ mr: 1 }}>
                            Pesquisando...
                          </Typography>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              filterOptions={(options) => options} // Não filtrar aqui, já filtramos na API
              noOptionsText={
                isSearchingProducts
                  ? "Pesquisando..."
                  : productSearchTerm.length < 2
                    ? "Digite pelo menos 2 caracteres para pesquisar"
                    : "Nenhum produto encontrado"
              }
              loading={isSearchingProducts}
            />
            {selectedProdutos.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Produtos Selecionados:
                </Typography>
                {selectedProdutos.map((produto) => (
                  <Box
                    key={produto.ID}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                      p: 1,
                      bgcolor: "grey.50",
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1 }}>
                      <strong>{produto.Nome}</strong>
                    </Typography>
                    {editingProductId !== produto.ID ? (
                      <>
                        <Typography variant="body2" color="primary.main">
                          R$ {getEffectivePrice(produto).toFixed(2)}
                        </Typography>
                        {produto.editedPrice !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{
                              textDecoration: "line-through",
                              color: "text.secondary",
                            }}
                          >
                            R$ {produto.PrecoVenda.toFixed(2)}
                          </Typography>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleEditProductPrice(produto)}
                          color="primary"
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => removeProduto(produto.ID)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <TextField
                          size="small"
                          value={tempProductPrice}
                          onChange={(e) => setTempProductPrice(e.target.value)}
                          placeholder="0,00"
                          sx={{ width: 120 }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                R$
                              </InputAdornment>
                            ),
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleSaveProductPrice(produto.ID)}
                          color="success"
                        >
                          <Save fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelProductPriceEdit}
                          color="error"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              label="Data do Atendimento"
              type="datetime-local"
              value={selectData}
              onChange={(e) => setSelectData(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid size={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Observações"
              value={historyNotes}
              onChange={(e) => setHistoryNotes(e.target.value)}
              placeholder="Descreva o problema, laudo ou observações do atendimento..."
            />
          </Grid>

          {/* Seção de Agendamento */}
          <Grid size={12}>
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "info.lighter",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "info.main",
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={createAgendamento}
                    onChange={(e) => setCreateAgendamento(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Criar Agendamento
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Agendar um retorno ou próximo serviço para este pet
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </Grid>

          {createAgendamento && (
            <>
              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Data do Agendamento *"
                  type="date"
                  value={agendamentoData}
                  onChange={(e) => setAgendamentoData(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  helperText="Data do próximo serviço"
                />
              </Grid>

              <Grid size={6}>
                <TextField
                  fullWidth
                  label="Hora do Agendamento *"
                  type="time"
                  value={agendamentoHora}
                  onChange={(e) => setAgendamentoHora(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  required
                  helperText="Horário do próximo serviço"
                />
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Observações do Agendamento"
                  value={agendamentoObservacoes}
                  onChange={(e) => setAgendamentoObservacoes(e.target.value)}
                  placeholder="Ex: Retorno para banho e tosa, trazer pet em jejum..."
                />
              </Grid>

              <Grid size={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={requiresPickup}
                      onChange={(e) => setRequiresPickup(e.target.checked)}
                    />
                  }
                  label="Requer busca do pet"
                />
              </Grid>
            </>
          )}

          {/* Seção de Pagamento */}
          <Grid size={12}>
            <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
              Informações de Pagamento
            </Typography>
          </Grid>

          <Grid size={6}>
            <TextField
              fullWidth
              label="Valor Pago"
              type="text"
              value={formatarMoeda(valorPago)}
              onChange={handleValorChange}
              onKeyDown={handleKeyDown}
              disabled={pagamentoQuitado}
              inputProps={{
                inputMode: "numeric",
              }}
              InputProps={{
                startAdornment: <Typography sx={{ mr: 1 }}>R$</Typography>,
              }}
              helperText={
                pagamentoQuitado
                  ? "Valor calculado automaticamente"
                  : "Digite o valor recebido (ex: 1234 = R$ 12,34)"
              }
            />
          </Grid>

          <Grid size={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={pagamentoQuitado}
                  onChange={(e) => setPagamentoQuitado(e.target.checked)}
                />
              }
              label="Pagamento Quitado"
            />
          </Grid>

          <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel>Forma de Pagamento</InputLabel>
              <Select
                value={formaPagamento}
                label="Forma de Pagamento"
                onChange={(e) => setFormaPagamento(e.target.value)}
              >
                <MenuItem value="Dinheiro">Dinheiro</MenuItem>
                <MenuItem value="Cartão de Crédito">Cartão de Crédito</MenuItem>
                <MenuItem value="Cartão de Débito">Cartão de Débito</MenuItem>
                <MenuItem value="PIX">PIX</MenuItem>
                {/* <MenuItem value="Transferência">Transferência</MenuItem> */}
                <MenuItem value="Boleto Bancário">Boleto Bancário</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* <Grid size={6}>
            <FormControl fullWidth>
              <InputLabel>Conta Bancária</InputLabel>
              <Select
                value={contaBancaria}
                label="Conta Bancária"
                onChange={(e) => setContaBancaria(e.target.value)}
              >
                <MenuItem value="Caixa Principal">Caixa Principal</MenuItem>
                <MenuItem value="Conta Corrente">Conta Corrente</MenuItem>
                <MenuItem value="Conta Poupança">Conta Poupança</MenuItem>
              </Select>
            </FormControl>
          </Grid> */}

          <Grid size={6}>
            <TextField
              fullWidth
              label="Data de Vencimento"
              type="date"
              value={dataVencimento}
              onChange={(e) => setDataVencimento(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              helperText="Data limite para pagamento"
            />
          </Grid>

          {selectedService && selectedPet && (
            <Grid size={12}>
              <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                {/* <Typography variant="subtitle2">
                  Resumo do Atendimento:
                </Typography> */}
                <Typography variant="body2">
                  <strong>Pet:</strong> {selectedPet.name} (
                  {selectedPet.tutor.name})
                </Typography>
                {/* <Typography variant="body2">
                  <strong>Serviço:</strong> {selectedService.Nome} - R${" "}
                  {selectedService.PrecoVenda.toFixed(2)}
                </Typography>
                {selectedProdutos.length > 0 && (
                  <>
                    <Typography variant="body2">
                      <strong>Produtos:</strong>
                    </Typography>
                    {selectedProdutos.map((produto) => (
                      <Typography
                        key={produto.ID}
                        variant="body2"
                        sx={{ ml: 2 }}
                      >
                        • {produto.Nome} - R$ {produto.PrecoVenda.toFixed(2)}
                      </Typography>
                    ))}
                  </>
                )} */}
                <Typography variant="body2">
                  <strong>Total:</strong> R${" "}
                  {(
                    getEffectivePrice(selectedService) +
                    selectedProdutos.reduce(
                      (sum, produto) => sum + getEffectivePrice(produto),
                      0
                    )
                  ).toFixed(2)}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || !selectedService || !selectedPet}
        >
          {isSubmitting ? "Criando..." : "Criar Atendimento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalNewAtendimento;
